import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18nService } from '../../../../core/i18n';
import { CompanyMembersFacade } from '../../../settings/services/company-members.facade';
import { TPipe } from '../../../../shared/pipes';
import { ToastService } from '../../../../shared/ui/toast/toast.service';
import { OnboardingStore } from '../../stores/onboarding.store';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
  selector: 'app-onboarding-step-invite',
  standalone: true,
  imports: [CommonModule, FormsModule, TPipe],
  template: `
    <div class="flex flex-col gap-5 px-1 py-2">
      <div>
        <p class="text-xs font-medium uppercase tracking-wide text-[var(--muted)]">
          {{ 'onboarding.progress' | t: { current: 3, total: 4 } }}
        </p>
        <h2 class="mt-1 text-xl font-semibold text-[var(--text)]">
          {{ 'onboarding.invite.title' | t }}
        </h2>
        <p class="mt-1 text-sm text-[var(--muted)]">
          {{ 'onboarding.invite.subtitle' | t }}
        </p>
      </div>

      <label class="flex flex-col gap-1">
        <span class="text-xs font-medium text-[var(--muted)]">
          {{ 'onboarding.invite.email' | t }}
        </span>
        <input
          type="email"
          autocomplete="email"
          inputmode="email"
          class="rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--text)] focus:border-[var(--accent)] focus:outline-none"
          [ngModel]="email()"
          (ngModelChange)="onEmailChange($event)"
          [disabled]="sending()"
          [attr.placeholder]="'onboarding.invite.emailplaceholder' | t"
        />
      </label>

      @if (errorMessage()) {
        <p class="text-sm text-[var(--danger)]">{{ errorMessage() }}</p>
      }

      <div class="mt-2 flex flex-wrap items-center justify-between gap-3">
        <div class="flex items-center gap-2">
          <button
            type="button"
            class="rounded-xl bg-[var(--accent)] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60 hover:opacity-90"
            [disabled]="!canSubmit() || sending()"
            (click)="sendInvite()"
          >
            {{ 'onboarding.invite.cta' | t }}
          </button>
          <button
            type="button"
            class="text-sm text-[var(--muted)] hover:text-[var(--text)] underline-offset-2 hover:underline"
            [disabled]="sending()"
            (click)="skipStep()"
          >
            {{ 'onboarding.invite.skip' | t }}
          </button>
        </div>
        <button
          type="button"
          class="text-xs text-[var(--muted)] hover:text-[var(--text)] underline-offset-2 hover:underline"
          [disabled]="sending()"
          (click)="snoozeNow()"
        >
          {{ 'onboarding.snooze' | t }}
        </button>
      </div>
    </div>
  `,
})
export class OnboardingStepInviteComponent {
  private readonly companyMembers = inject(CompanyMembersFacade);
  private readonly store = inject(OnboardingStore);
  private readonly toast = inject(ToastService);
  private readonly i18n = inject(I18nService);

  protected readonly email = signal('');
  protected readonly sending = signal(false);
  protected readonly errorMessage = signal<string | null>(null);
  protected readonly canSubmit = computed(() => EMAIL_REGEX.test(this.email().trim()));

  onEmailChange(value: string): void {
    this.email.set(value);
    if (this.errorMessage()) this.errorMessage.set(null);
  }

  async sendInvite(): Promise<void> {
    if (!this.canSubmit() || this.sending()) return;
    const target = this.email().trim();

    this.sending.set(true);
    this.errorMessage.set(null);
    try {
      await this.companyMembers.inviteMember(target, 'Member');
      this.toast.success(this.i18n.t('onboarding.invite.toastsuccess', { email: target }));
      await this.store.advance(3);
    } catch (err: unknown) {
      const fallback = this.i18n.t('onboarding.invite.errorgeneric');
      const message =
        typeof err === 'object' && err && 'message' in err && typeof (err as { message: unknown }).message === 'string'
          ? (err as { message: string }).message
          : fallback;
      this.errorMessage.set(message);
    } finally {
      this.sending.set(false);
    }
  }

  async skipStep(): Promise<void> {
    if (this.sending()) return;
    await this.store.advance(3);
  }

  async snoozeNow(): Promise<void> {
    if (this.sending()) return;
    await this.store.snooze();
  }
}
